import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Portal from '@mui/material/Portal';
import { keyframes } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

import { useSocket } from '../../api/socket/SocketProvider';
import { getAvatarColor, getInitials } from '../../routes/Messaging/utils';
import { emojiFontFamily } from '../../routes/Messaging/utils/emoji';
import { previewText } from '../../routes/Messaging/utils/messagePreview';
import type { Message } from '../../routes/Messaging/types';
import { decideAlert } from './tabPresence';
import {
    getPopupPrefs,
    setPopupPrefs,
    subscribeActiveConversation,
    subscribePopupPrefs,
    type PopupPrefs
} from './messagePopupState';

// Outlook's desktop alert stays up ~7s by default; hovering holds it.
const DISPLAY_MS = 7000;
const MAX_CARDS = 3;

type NotifyConversation = {
    type: 'direct' | 'group' | 'document';
    name: string | null;
    documentType: string | null;
    documentNumber: number | null;
    documentId: number | null;
};
type NotifyPayload = { conversationId: number; message: Message; conversation?: NotifyConversation };

type Card = {
    key: number;
    conversationId: number;
    title: string;
    sender: string;
    picture?: string;
    text: string;
    isGroup: boolean;
    count: number;
};

const conversationTitle = ({ message, conversation }: NotifyPayload) => {
    if (conversation?.type === 'group') return conversation.name || 'Group';
    if (conversation?.type === 'document') {
        return `${conversation.documentType || 'Document'} #${conversation.documentNumber || conversation.documentId}`;
    }
    return message.userName;
};

// Asked once per browser: a dismissed prompt that keeps coming back gets the
// site auto-blocked by Chrome. After that, the settings menu is the way in.
const PERMISSION_ASKED_KEY = 'message-popup-permission-asked';

const canUseSystemNotifications = () => typeof window !== 'undefined' && 'Notification' in window;

// A short two-note chime, generated so there is no audio asset to ship.
let audioCtx: AudioContext | null = null;

// Safari keeps audio muted unless it was started from a click or key press, and
// a message arrives with neither — so open the audio on the first interaction.
const unlockAudio = () => {
    try {
        audioCtx = audioCtx || new AudioContext();
        if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
        if (audioCtx.state === 'running') {
            document.removeEventListener('pointerdown', unlockAudio);
            document.removeEventListener('keydown', unlockAudio);
        }
    } catch {
        // no audio in this browser
    }
};
if (typeof document !== 'undefined') {
    document.addEventListener('pointerdown', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
}

const playChime = () => {
    try {
        audioCtx = audioCtx || new AudioContext();
        if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
        const now = audioCtx.currentTime;
        [880, 1320].forEach((freq, i) => {
            const osc = audioCtx!.createOscillator();
            const gain = audioCtx!.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const start = now + i * 0.12;
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(0.15, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.25);
            osc.connect(gain).connect(audioCtx!.destination);
            osc.start(start);
            osc.stop(start + 0.3);
        });
    } catch {
        // audio unavailable — the popup still shows
    }
};

// ── Look: a dark, quiet card — near-black glass, light type, one hairline
// border and a single accent. Dark in both themes, like an OS notification,
// so it reads as "from outside the page" wherever it lands.
const INK = {
    bg: 'rgba(16, 17, 20, 0.92)',
    border: 'rgba(255, 255, 255, 0.08)',
    title: '#F4F4F5',
    text: '#A1A1AA',
    faint: '#71717A',
    accent: '#3B82F6'
} as const;

const riseIn = keyframes`
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
`;
const drain = keyframes`
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
`;

const inkButton = {
    width: 24,
    height: 24,
    color: INK.faint,
    '&:hover': { color: INK.title, bgcolor: 'rgba(255, 255, 255, 0.08)' }
} as const;

const PopupCard: FC<{
    card: Card;
    soundMuted: boolean;
    onOpen: (card: Card) => void;
    onClose: (key: number) => void;
}> = ({ card, soundMuted, onOpen, onClose }) => {
    const [hovered, setHovered] = useState(false);
    const [pageVisible, setPageVisible] = useState(() => document.visibilityState === 'visible');

    useEffect(() => {
        const onVisibility = () => setPageVisible(document.visibilityState === 'visible');
        document.addEventListener('visibilitychange', onVisibility);
        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    // Held while hovered or while the tab is hidden, so it is never missed.
    // Restarts when the card is refreshed by a newer message.
    useEffect(() => {
        if (hovered || !pageVisible) return;
        const timer = setTimeout(() => onClose(card.key), DISPLAY_MS);
        return () => clearTimeout(timer);
    }, [hovered, pageVisible, card.key, card.count, onClose]);

    const paused = hovered || !pageVisible;

    return (
        <Box
            role="alert"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => onOpen(card)}
            sx={{
                position: 'relative',
                width: 360,
                maxWidth: 'calc(100vw - 32px)',
                borderRadius: '14px',
                overflow: 'hidden',
                cursor: 'pointer',
                pointerEvents: 'auto',
                bgcolor: INK.bg,
                border: `1px solid ${INK.border}`,
                backdropFilter: 'blur(20px) saturate(140%)',
                WebkitBackdropFilter: 'blur(20px) saturate(140%)',
                boxShadow: '0 24px 48px -12px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.2)',
                color: INK.title,
                animation: `${riseIn} 280ms cubic-bezier(.16, 1, .3, 1) both`,
                transition: 'border-color 160ms ease, transform 160ms ease',
                '&:hover': { borderColor: 'rgba(255, 255, 255, 0.16)', transform: 'translateY(-1px)' },
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    '&:hover': { transform: 'none' }
                }
            }}
        >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: '14px 12px 16px 14px' }}>
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar
                        src={card.picture}
                        sx={{
                            width: 38,
                            height: 38,
                            fontSize: 13,
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                            bgcolor: getAvatarColor(card.sender),
                            boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.12)'
                        }}
                    >
                        {getInitials(card.sender)}
                    </Avatar>
                    {/* unread dot */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -1,
                            right: -1,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: INK.accent,
                            border: '2px solid #101114'
                        }}
                    />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography
                            dir="auto"
                            noWrap
                            sx={{ fontSize: 14, fontWeight: 600, color: INK.title, minWidth: 0 }}
                        >
                            {card.title}
                        </Typography>
                        <Typography
                            component="span"
                            sx={{ fontSize: 11.5, color: INK.faint, flexShrink: 0, ml: 'auto' }}
                        >
                            {card.count > 1 ? `${card.count} new · now` : 'now'}
                        </Typography>
                    </Box>
                    <Typography
                        dir="auto"
                        sx={{
                            mt: 0.4,
                            fontSize: 13,
                            fontWeight: 400,
                            fontFamily: emojiFontFamily,
                            lineHeight: 1.45,
                            color: INK.text,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word'
                        }}
                    >
                        {card.isGroup && (
                            <Box component="span" sx={{ color: INK.title, fontWeight: 500 }}>
                                {card.sender}:{' '}
                            </Box>
                        )}
                        {card.text}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.25, mt: '-2px' }}>
                    <IconButton
                        size="small"
                        title={soundMuted ? 'Turn sound on' : 'Mute sound'}
                        sx={inkButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            setPopupPrefs({ soundMuted: !soundMuted });
                        }}
                    >
                        {soundMuted ? (
                            <VolumeOffIcon sx={{ fontSize: 15 }} />
                        ) : (
                            <VolumeUpIcon sx={{ fontSize: 15 }} />
                        )}
                    </IconButton>
                    <IconButton
                        size="small"
                        title="Close"
                        sx={inkButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose(card.key);
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Countdown — keyed on count so a newer message refills it */}
            <Box
                key={card.count}
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 2,
                    bgcolor: INK.accent,
                    opacity: 0.7,
                    transformOrigin: 'left',
                    animation: `${drain} ${DISPLAY_MS}ms linear forwards`,
                    animationPlayState: paused ? 'paused' : 'running'
                }}
            />
        </Box>
    );
};

/**
 * Outlook-style alert for new chat messages.
 * Window in focus → a card in the bottom-right corner (max 3 stacked).
 * Window minimised / behind another app → an OS notification, so it is seen
 * outside the browser. Clicking either opens the conversation.
 */
const MessagePopup: FC = () => {
    const { socket } = useSocket();
    const history = useHistory();
    const [cards, setCards] = useState<Card[]>([]);
    const [prefs, setPrefs] = useState<PopupPrefs>(getPopupPrefs);
    const prefsRef = useRef(prefs);
    const keyRef = useRef(0);

    useEffect(() => subscribePopupPrefs(setPrefs), []);
    useEffect(() => {
        prefsRef.current = prefs;
        if (prefs.popupsMuted) setCards([]);
    }, [prefs]);

    // Browsers only show the permission prompt from a user gesture, so ask on
    // the first click anywhere in the app — once per browser.
    useEffect(() => {
        if (!canUseSystemNotifications() || Notification.permission !== 'default') return;
        try {
            if (localStorage.getItem(PERMISSION_ASKED_KEY)) return;
        } catch {
            return;
        }
        const ask = () => {
            if (!prefsRef.current.popupsMuted && Notification.permission === 'default') {
                try {
                    localStorage.setItem(PERMISSION_ASKED_KEY, '1');
                } catch {
                    // ignore
                }
                Notification.requestPermission().catch(() => {});
            }
        };
        document.addEventListener('click', ask, { once: true });
        return () => document.removeEventListener('click', ask);
    }, []);

    const openConversation = useCallback(
        (conversationId: number) => {
            history.push(`/messaging?c=${conversationId}`);
        },
        [history]
    );

    const closeCard = useCallback((key: number) => {
        setCards((prev) => prev.filter((c) => c.key !== key));
    }, []);

    useEffect(() => {
        if (!socket) return;
        const handleNotify = (data: NotifyPayload) => {
            const { popupsMuted, soundMuted } = prefsRef.current;
            if (popupsMuted || !data?.message) return;

            // Skips the conversation being read, and lets only one tab alert.
            const systemAllowed = canUseSystemNotifications() && Notification.permission === 'granted';
            const decision = decideAlert(data.conversationId, systemAllowed);
            if (decision === 'skip') return;

            const title = conversationTitle(data);
            const isGroup = data.conversation?.type === 'group' || data.conversation?.type === 'document';
            const sender = data.message.userName || 'Unknown';
            const text = previewText(data.message);

            if (decision === 'system') {
                try {
                    // A plain object, not an inline literal: `renotify` is missing from
                    // TS's DOM typings, and only inline literals get excess-property checks.
                    const options = {
                        body: isGroup ? `${sender}: ${text}` : text,
                        icon: data.message.profilePicture || '/app-icon-192.png',
                        // One OS notification per conversation — newer replaces older,
                        // and several open tabs don't stack duplicates.
                        tag: `taytan-chat-${data.conversationId}`,
                        // Without it a same-tag replacement is silent and shows no banner,
                        // so only the first message of a chat was ever announced.
                        renotify: true,
                        // Our own chime plays below; the OS one would double it, and is
                        // lost entirely if macOS/Windows mutes the browser.
                        silent: true
                    };
                    const n = new Notification(title, options);
                    if (!soundMuted) playChime();
                    n.onerror = (e) => console.log('[message-popup] system notification error', e);
                    n.onclick = () => {
                        window.focus();
                        openConversation(data.conversationId);
                        n.close();
                    };
                    return;
                } catch (err) {
                    // fall through to the in-app card
                    console.log('[message-popup] system notification failed, showing card', err);
                }
            }

            if (!soundMuted) playChime();
            setCards((prev) => {
                const existing = prev.find((c) => c.conversationId === data.conversationId);
                const card: Card = {
                    key: existing?.key ?? ++keyRef.current,
                    conversationId: data.conversationId,
                    title,
                    sender,
                    picture: data.message.profilePicture || undefined,
                    text,
                    isGroup,
                    count: (existing?.count || 0) + 1
                };
                const others = prev.filter((c) => c.conversationId !== data.conversationId);
                return [...others, card].slice(-MAX_CARDS);
            });
        };

        socket.on('message:notify', handleNotify);
        return () => {
            socket.off('message:notify', handleNotify);
        };
    }, [socket, openConversation]);

    // Opening the conversation elsewhere (e.g. from the chat list) clears its card.
    useEffect(
        () =>
            subscribeActiveConversation((id) => {
                if (id) setCards((prev) => prev.filter((c) => c.conversationId !== id));
            }),
        []
    );

    if (!cards.length) return null;

    // Portal: the Header's AppBar is its own stacking context, which would
    // otherwise put the cards underneath every open dialog.
    return (
        <Portal>
            <Box
                sx={{
                    position: 'fixed',
                    right: 16,
                    bottom: 16,
                    zIndex: (theme) => theme.zIndex.snackbar,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    pointerEvents: 'none'
                }}
            >
                {cards.map((card) => (
                    <PopupCard
                        key={card.key}
                        card={card}
                        soundMuted={prefs.soundMuted}
                        onOpen={(c) => {
                            closeCard(c.key);
                            openConversation(c.conversationId);
                        }}
                        onClose={closeCard}
                    />
                ))}
            </Box>
        </Portal>
    );
};

export default MessagePopup;
